import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import "../../../App.css";
import titleImage from "../../../assets/TitleLeaderboards.png";

const LEADERBOARD_SECTIONS = [
  { key: "xp", title: "XP", valueLabel: "XP" },
  { key: "merits", title: "MERITS", valueLabel: "MERITS" },
  { key: "wealth", title: "WEALTH", valueLabel: "POGS" },
  { key: "kills", title: "KILLS", valueLabel: "KILLS" },
  { key: "heals", title: "HEALS", valueLabel: "HEALS" },
  { key: "hacks", title: "HACKS", valueLabel: "HACKS" },
];

function formatNumber(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number.toLocaleString() : "0";
}

function ordinal(rank) {
  const mod100 = rank % 100;

  if (mod100 >= 11 && mod100 <= 13) {
    return `${rank}th`;
  }

  if (rank % 10 === 1) return `${rank}st`;
  if (rank % 10 === 2) return `${rank}nd`;
  if (rank % 10 === 3) return `${rank}rd`;
  return `${rank}th`;
}

function LeaderboardRow({ entry, rank, valueLabel }) {
  return (
    <div className={`leaderboards-row leaderboards-rank-${rank}`}>
      <div className="leaderboards-position" aria-label={`Rank ${rank}`}>
        {ordinal(rank)}
      </div>

      <div className="leaderboards-username" title={entry.username}>
        {entry.username}
      </div>

      <div className="leaderboards-value">
        <span>{formatNumber(entry.value)}</span>
        <small>{valueLabel}</small>
      </div>
    </div>
  );
}

function LeaderboardSection({ title, valueLabel, entries }) {
  const topThree = entries.slice(0, 3);
  const remaining = entries.slice(3, 10);

  return (
    <section className="leaderboards-section" aria-labelledby={`leaderboard-${title.toLowerCase()}`}>
      <div className="leaderboards-section-header">
        <div className="leaderboards-section-kicker">TOP 10</div>
        <h2 id={`leaderboard-${title.toLowerCase()}`} className="leaderboards-section-title">
          {title}
        </h2>
      </div>

      <div className="leaderboards-list">
        {topThree.length > 0 ? (
          topThree.map((entry, index) => (
            <LeaderboardRow
              key={`${index + 1}-${entry.username}`}
              entry={entry}
              rank={index + 1}
              valueLabel={valueLabel}
            />
          ))
        ) : (
          <div className="leaderboards-empty">NO RANKINGS AVAILABLE</div>
        )}
      </div>

      {remaining.length > 0 && (
        <details className="leaderboards-more">
          <summary>
            <span className="leaderboards-more-closed">SHOW 4TH–10TH</span>
            <span className="leaderboards-more-open">HIDE 4TH–10TH</span>
          </summary>

          <div className="leaderboards-more-list">
            {remaining.map((entry, index) => (
              <LeaderboardRow
                key={`${index + 4}-${entry.username}`}
                entry={entry}
                rank={index + 4}
                valueLabel={valueLabel}
              />
            ))}
          </div>
        </details>
      )}
    </section>
  );
}

const Leaderboards = () => {
  const baseURL = window.location.origin;
  const [leaderboards, setLeaderboards] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    const loadLeaderboards = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await fetch("/api/leaderboards", {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Leaderboard request failed with status ${response.status}`);
        }

        const payload = await response.json();

        if (active) {
          setLeaderboards(payload?.leaderboards || {});
        }
      } catch (requestError) {
        console.error("Failed to load Pogforts leaderboards:", requestError);

        if (active) {
          setError("LEADERBOARDS ARE CURRENTLY UNAVAILABLE");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadLeaderboards();

    return () => {
      active = false;
    };
  }, []);

  const sections = useMemo(
    () =>
      LEADERBOARD_SECTIONS.map((section) => ({
        ...section,
        entries: Array.isArray(leaderboards?.[section.key])
          ? leaderboards[section.key]
          : [],
      })),
    [leaderboards]
  );

  return (
    <div className="home-container leaderboards-page">
      <Helmet>
        <title>Leaderboards - Pogforts</title>
        <meta
          name="description"
          content="Pogforts top 10 leaderboards for XP, merits, wealth, kills, heals, and successful hacks."
        />
        <meta property="og:title" content="Leaderboards - Pogforts" />
        <meta
          property="og:description"
          content="See the top Pogforts players across XP, merits, wealth, kills, heals, and hacks."
        />
        <meta property="og:image" content={`${baseURL}/assets/PogfortsHome.png`} />
        <meta property="og:url" content={`${baseURL}/leaderboards`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Leaderboards - Pogforts" />
        <meta
          name="twitter:description"
          content="See the top Pogforts players across XP, merits, wealth, kills, heals, and hacks."
        />
        <meta name="twitter:image" content={`${baseURL}/assets/PogfortsHome.png`} />
      </Helmet>

      <h1 className="hidden-title">Pogforts Leaderboards</h1>

      <img
        src={titleImage}
        alt="Pogforts Leaderboards"
        className="featured-image leaderboards-title-image"
      />

      <div className="leaderboards-shell">
        {loading && <div className="leaderboards-status">LOADING LEADERBOARDS...</div>}

        {!loading && error && (
          <div className="leaderboards-status leaderboards-status-error">{error}</div>
        )}

        {!loading && !error && (
          <div className="leaderboards-sections">
            {sections.map((section) => (
              <LeaderboardSection
                key={section.key}
                title={section.title}
                valueLabel={section.valueLabel}
                entries={section.entries}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Leaderboards;
